import type { Dispatch, SetStateAction } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Icon } from '../index';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';
import { unitWord } from '../weightUnitLabels';
import { stateAria } from "../a11y";

// Weight-stepper bar (inbox 024b2c93). Rendered only when the weight
// cell is focused so the row stays compact otherwise. Plates icon + unit
// toggle + ± sign + magnitude buttons. Per
// feedback_user_visual_accessibility all tap targets stay ≥44pt and the
// input itself is never shrunk to make room for them.
export function SetRowStepperBar({
  unit,
  onOpenPlates,
  onToggleUnit,
  stepSign,
  setStepSign,
  stepMagnitudes,
  applyDelta,
  theme,
}: {
  unit: 'kg' | 'lb';
  onOpenPlates?: () => void;
  /** Absent ⇒ no unit chip. PE-35: in the program editor the unit belongs to
   *  the DRAFT (buildPayload's cellUnit), not to one set, so a per-set toggle
   *  there would fragment the one unit the build can read. */
  onToggleUnit?: () => void;
  stepSign: 1 | -1;
  setStepSign: Dispatch<SetStateAction<1 | -1>>;
  stepMagnitudes: number[];
  applyDelta: (delta: number) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  return (
    <View style={styles.stepperRow}>
      {onOpenPlates ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('mobile.logger.plates')}
          onPress={onOpenPlates}
          hitSlop={8}
          style={({ pressed }) => [
            styles.stepperButton,
            styles.stepperButtonPlates,
            pressed && styles.stepperButtonPressed,
          ]}
        >
          <Icon
            name="weight"
            size={theme.typography.sizes.sm}
            color={theme.palette.ink2}
          />
        </Pressable>
      ) : null}
      {onToggleUnit ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('mobile.logger.unitA11y', { unit })}
          onPress={onToggleUnit}
          hitSlop={8}
          style={({ pressed }) => [
            styles.stepperButton,
            styles.stepperButtonUnit,
            pressed && styles.stepperButtonPressed,
          ]}
        >
          <Text style={[styles.stepperButtonText, styles.stepperButtonTextUnit]}>
            {/* DS-83: `unitWord`, CN-26's single source for "how this app
                spells kg/lb", not a raw JS uppercase of the canonical unit:
                that left "LB" latin here beside "ФУНТ" cyrillic on the
                weight-field label two rows up (FocusPanelInputs.tsx). */}
            {unitWord(t, unit).toUpperCase()}
          </Text>
        </Pressable>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: stepSign === -1 }}
        {...stateAria({ selected: stepSign === -1 }, "button")}
        accessibilityLabel={
          stepSign === 1
            ? t('mobile.logger.stepAdd', { defaultValue: 'Step up' })
            : t('mobile.logger.stepSubtract', { defaultValue: 'Step down' })
        }
        onPress={() => setStepSign((s) => (s === 1 ? -1 : 1))}
        hitSlop={8}
        style={({ pressed }) => [
          styles.stepperButton,
          styles.stepperButtonSign,
          stepSign === 1
            ? styles.stepperButtonSignPositive
            : styles.stepperButtonSignNegative,
          pressed && styles.stepperButtonPressed,
        ]}
      >
        <Text
          style={[
            styles.stepperButtonText,
            // DS-85 — il segno si vede prima di essere letto: bordo doppio
            // sulla cella, glifo più grande qui.
            styles.stepperButtonTextSign,
            stepSign === 1
              ? styles.stepperButtonTextSignPositive
              : styles.stepperButtonTextSignNegative,
          ]}
        >
          {stepSign === 1 ? '+' : '−'}
        </Text>
      </Pressable>
      {stepMagnitudes.map((magnitude) => {
        const label = magnitude.toString();
        return (
          <Pressable
            key={magnitude}
            accessibilityRole="button"
            accessibilityLabel={`${stepSign === 1 ? '+' : '−'}${label}`}
            onPress={() => applyDelta(magnitude * stepSign)}
            hitSlop={8}
            style={({ pressed }) => [
              styles.stepperButton,
              pressed && styles.stepperButtonPressed,
            ]}
          >
            <Text style={styles.stepperButtonText}>{label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}
